import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@/environments/environment';
import {
  CreateChildAccountModel,
  DeliveryAddressModel,
  EmployeeModel,
  RemoveChildAccountModel,
  Role,
  TaxCheckResponse,
  UpdateAddressStatusRequest,
  User,
  UserGiftExchanged,
  UserPagingModel,
  ImportCsStaffJob,
  ImportCsStaffResultPage,
  ImportCsStaffReadingResult,
} from '../interfaces/user.interface';
import { ResponsePagingModel } from '../interfaces/pagination.interface';
import { Observable } from 'rxjs';
import { BaseResponseDataModel, BaseResponseModel } from '../interfaces/base-response.interface';

export interface AssignRoleResponse {
  status: boolean;
  successCount: number;
  failureCount: number;
  failedUserNames: string[];
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private http: HttpClient) { }

  getUsers(body: UserPagingModel): Observable<ResponsePagingModel<User>>
  getUsers(body: string): Observable<ResponsePagingModel<User>>
  getUsers(body: unknown): Observable<ResponsePagingModel<User>> {
    let request: string;
    if (typeof body === 'object') {
      request = JSON.stringify(body);
    } else {
      request = body as string;
    }
    return this.http.post<ResponsePagingModel<User>>(`${environment.baseURL}user/user/all`, request, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  getUser(username: string) {
    return this.http.get<User>(`${environment.baseURL}user/user/${username}`, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    });
  }

  updateUserBusinessProfile(payload: any) {
    return this.http.put(
      `${environment.baseURL}user/user/admin-update-business-profile`,
      payload,
      {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
      }
    );
  }

  confirmUser(payload: any) {
    return this.http.put(
      `${environment.baseURL}user/user/confirm-user-business-profile`,
      payload,
      {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
      }
    );
  }

  getLoggedUser(): Observable<User> {
    const token = localStorage.getItem('token');
    return this.http.get<User>(`${environment.baseURL}user/user`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  getUserAdmin(username: string, useCache?: boolean) {
    return this.http.get<User>(`${environment.baseURL}user/user/admin/${encodeURIComponent(username)}`, {
      params: useCache === undefined ? {} : { useCache },
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    });
  }

  getAllRoles() {
    return this.http.get<Role[]>(`${environment.baseURL}user/role`);
  }

  createUser(body: string) {
    return this.http.post<boolean>(`${environment.baseURL}user/user/create`, body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  checkUserExist(username: string) {
    return this.http.get<boolean>(`${environment.baseURL}user/user/validate-user-login?userName=${username}`);
  }

  updateUserAdmin(body: string) {
    return this.http.put<BaseResponseModel>(`${environment.baseURL}user/user/admin-update`, body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  updateUserSupportAdmin(body: any) {
    return this.http.put<BaseResponseModel>(`${environment.baseURL}user/user/admin-update-user-support`, body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  resetUserPass(body: string) {
    return this.http.put<boolean>(`${environment.baseURL}user/user/reset-password`, body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  getUserAddress() {
    return this.http.get<DeliveryAddressModel>(`${environment.baseURL}user/user/address`);
  }

  allowParent(userName: string): Observable<BaseResponseModel> {
    return this.http.post<BaseResponseModel>(`${environment.baseURL}user/user/v1/admin/allow-parent`, JSON.stringify(userName), {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
  disAllowParent(userName: string): Observable<BaseResponseModel> {
    return this.http.delete<BaseResponseModel>(`${environment.baseURL}user/user/v1/admin/allow-parent`, {
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userName)
    });
  }

  createChildUser(body: CreateChildAccountModel): Observable<BaseResponseDataModel<User>> {
    return this.http.post<BaseResponseDataModel<User>>(`${environment.baseURL}user/user/v1/admin/create-child-user`, JSON.stringify(body), {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  removeChildAccount(body: RemoveChildAccountModel): Observable<BaseResponseModel> {

    return this.http.post<BaseResponseModel>(`${environment.baseURL}user/user/admin/remove-child-account`, JSON.stringify(body), {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  getMembershipTypes() {
    return this.http.get<any>(`${environment.baseURL}user/user/get-membership-types`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  exportUser(body: UserPagingModel): Observable<any[]> {
    return this.http.post<any[]>(`${environment.baseURL}user/user/admin/export-users`, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  getEmployees(search?: string): Observable<BaseResponseDataModel<EmployeeModel[]>> {
    return this.http.post<BaseResponseDataModel<EmployeeModel[]>>(`${environment.baseURL}user/employee/v1/get-employees`, {
      searchTerm: search ?? "",
      limit: 20000,
      offset: 0,
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  getPharmacyChains(): Observable<import('../interfaces/user.interface').PharmacyChain[]> {
    return this.http.get<import('../interfaces/user.interface').PharmacyChain[]>(`${environment.baseURL}user/user/admin/pharmacy-chains`, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    });
  }

  getCustomerGroups(): Observable<import('../interfaces/user.interface').CustomerGroup[]> {
    return this.http.get<import('../interfaces/user.interface').CustomerGroup[]>(`${environment.baseURL}user/user/admin/customer-groups`, {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
    });
  }

  getEmployeesV2(search?: string): Observable<ResponsePagingModel<EmployeeModel>> {
    return this.http.post<ResponsePagingModel<EmployeeModel>>(`${environment.baseURL}user/employee/v2/get-employees`, {
      searchTerm: search ?? "",
      limit: 20000,
      offset: 0,
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  confirmVatActive(taxCode: string): Observable<BaseResponseModel> {
    return this.http.post<BaseResponseModel>(
      `${environment.baseURL}user/user/v1/confirm-vat-active`,
      JSON.stringify(taxCode),
      { headers: { 'Content-Type': 'application/json' } }
    );
  }
  removeVatActive(taxCode: string): Observable<BaseResponseModel> {
    return this.http.post<BaseResponseModel>(
      `${environment.baseURL}user/user/v1/remove-vat-active`,
      JSON.stringify(taxCode),
      { headers: { 'Content-Type': 'application/json' } }
    );
  }

  syncEmployeeData() {
    return this.http.post(`${environment.baseURL}user/employee/sync-from-erp`, {});
  }

  getUserGiftsExchanged(username: string) {
    return this.http.get<ResponsePagingModel<UserGiftExchanged>>(`${environment.baseURL}user/user/admin/gift-exchange/${username}`);
  }

  checkTaxCodeAvailable(taxCode: string): Observable<TaxCheckResponse> {
    return this.http.post<TaxCheckResponse>(
      `${environment.baseURL}user/user/get-tax-validation-result-by-admin`,
      `"${taxCode}"`,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }
  /** Tạo mới địa chỉ giao hàng cho user */
  createDeliveryAddress(payload: any): Observable<BaseResponseModel> {
    return this.http.post<BaseResponseModel>(
      `${environment.baseURL}user/user/admin/delivery-address`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  /** Cập nhật địa chỉ giao hàng */
  updateDeliveryAddress(payload: any): Observable<BaseResponseModel> {
    return this.http.put<BaseResponseModel>(
      `${environment.baseURL}user/user/admin/delivery-address`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  /** Xóa địa chỉ giao hàng */
  deleteDeliveryAddress(userName: string, addressId: string): Observable<BaseResponseModel> {
    return this.http.delete<BaseResponseModel>(
      `${environment.baseURL}user/user/admin/delivery-address/user/${userName}/addresses/${addressId}`
    );
  }

  /** Cập nhật trạng thái phê duyệt địa chỉ (Duyệt / Từ chối)
 */
  updateDeliveryAddressStatus(payload: UpdateAddressStatusRequest): Observable<BaseResponseModel> {
    return this.http.post<BaseResponseModel>(
      `${environment.baseURL}user/user/admin/delivery-address-status`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
      }
    );
  }

  skipAutoUpdateBusinessName(payload: string[]) {
    return this.http.patch<BaseResponseModel>(`${environment.baseURL}user/user/admin/user-business-name`, payload);
  }

  importCsStaffReading(formData: FormData): Observable<BaseResponseDataModel<ImportCsStaffReadingResult>> {
    return this.http.post<BaseResponseDataModel<ImportCsStaffReadingResult>>(`${environment.baseURL}user/importCsStaff/v1/import-cs-staff-reading`, formData);
  }

  assignRole(payload: { userNames: string[], roleName: string }): Observable<AssignRoleResponse> {
    return this.http.post<AssignRoleResponse>(
      `${environment.baseURL}user/user/assign-role`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  importCsStaffSave(formData: FormData): Observable<BaseResponseDataModel<ImportCsStaffJob>> {
    return this.http.post<BaseResponseDataModel<ImportCsStaffJob>>(`${environment.baseURL}user/importCsStaff/v1/import-cs-staff-saving`, formData);
  }

  getImportCsStaffJob(jobId: string): Observable<BaseResponseDataModel<ImportCsStaffJob>> {
    return this.http.get<BaseResponseDataModel<ImportCsStaffJob>>(`${environment.baseURL}user/importCsStaff/v1/import-cs-staff-jobs/${encodeURIComponent(jobId)}`);
  }

  getImportCsStaffResults(jobId: string, isSuccess: boolean, page: number, pageSize: number): Observable<BaseResponseDataModel<ImportCsStaffResultPage>> {
    return this.http.get<BaseResponseDataModel<ImportCsStaffResultPage>>(`${environment.baseURL}user/importCsStaff/v1/import-cs-staff-jobs/${encodeURIComponent(jobId)}/results`, {
      params: {
        isSuccess: String(isSuccess),
        page: String(page),
        pageSize: String(pageSize),
      }
    });
  }

  downloadImportCsStaffResults(jobId: string, isSuccess: boolean): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.baseURL}user/importCsStaff/v1/import-cs-staff-jobs/${encodeURIComponent(jobId)}/results/export`, {
      params: {
        isSuccess: String(isSuccess),
      },
      observe: 'response',
      responseType: 'blob'
    });
  }
}
